/**
 * Pure helpers for SPEC-invoice-merge-2026-08-17 R3 (merge UI).
 *
 * Deliberately dependency-free (no react-query, no api import) so these are
 * unit-testable in isolation and reusable by both InvoiceListPage and
 * MergeConfirmationDialog's tests.
 *
 * SPEC-invoice-merge-history-2026-10-02 R4 AC7: what is left here is DISPLAY and SELECTION only, kept for the
 * legacy confirmation that still serves tenants without the `reviewed_invoice_merge` flag. The reviewed path
 * (components/invoices/InvoiceMergeReview.tsx) uses none of it: lifecycle, payment, survivor and balance
 * decisions there come from the server plan (./invoice-merge-plan). No eligibility rule lives in this file.
 */

// Server-owned merge plan parser (SPEC-invoice-merge-history-2026-10-02 R1).
export * from './invoice-merge-plan';

export interface MergeCandidateInvoice {
  id: string;
  invoice_number: string;
  created_at?: string | null;
}

/**
 * Mirrors R1's `determine_survivor` rule (backend
 * services/invoice_merge_eligibility.py) so the confirmation can show which
 * number will survive BEFORE the merge endpoint is ever called (AC4 requires
 * this to be known pre-commit, and there is no separate preview endpoint --
 * see output.md).
 *
 * This is a PREVIEW ONLY. The merge endpoint's own response
 * (survivor_invoice_number / absorbed_invoice_number) is the authoritative
 * answer; this function exists solely so the confirmation dialog has
 * something correct to show before that call is made. Rule, in order:
 *   1. If both invoice_number values are purely numeric, the lower number
 *      survives.
 *   2. Otherwise, the invoice with the earlier created_at survives.
 *   3. Otherwise (missing/tied created_at), the lexicographically smaller
 *      id survives, purely for determinism.
 *
 * Returns [survivor, absorbed].
 */
export function determineMergeSurvivor<T extends MergeCandidateInvoice>(
  a: T,
  b: T
): [T, T] {
  const aNum = parseInvoiceNumberAsInt(a.invoice_number);
  const bNum = parseInvoiceNumberAsInt(b.invoice_number);
  if (aNum !== null && bNum !== null && aNum !== bNum) {
    return aNum < bNum ? [a, b] : [b, a];
  }

  if (a.created_at && b.created_at && a.created_at !== b.created_at) {
    return a.created_at < b.created_at ? [a, b] : [b, a];
  }

  return a.id <= b.id ? [a, b] : [b, a];
}

function parseInvoiceNumberAsInt(value: string): number | null {
  const trimmed = value.trim();
  if (!/^-?\d+$/.test(trimmed)) {
    return null;
  }
  return parseInt(trimmed, 10);
}

export interface MergeRefusal {
  code?: string;
  message: string;
}

/**
 * Extracts R1's specific refusal reason (or any other error) from a failed
 * merge mutation.
 *
 * main.py's global `@app.exception_handler(HTTPException)` rewrites EVERY
 * error body to `{"error": exc.detail, "status_code":..., "timestamp":...}`
 * (confirmed in R2-merge-execution's output.md). For a 409 refusal,
 * `exc.detail` is itself `{"error": "merge_refused", "code": <R1 code>,
 * "message": <R1 reason>}` (routers/invoices.py `merge_invoices`). For a 404
 * (invoice not found / cross-tenant), `exc.detail` is a plain string.
 *
 * `fetchAPI` (lib/api.ts) stores the raw parsed body on `error.data` but its
 * generic error-message extraction only reads `errorData.detail` /
 * `errorData.error` / `errorData.message` as flat strings -- it does not
 * unwrap a nested object under `errorData.error`, so `error.message` alone
 * would stringify to "[object Object]" for a 409 refusal. This function does
 * the unwrapping the caller needs to show the ACTUAL specific reason (AC7)
 * rather than a generic denial.
 */
export function extractMergeRefusal(error: any): MergeRefusal {
  const nested = error?.data?.error;

  if (nested && typeof nested === 'object') {
    return {
      code: nested.code,
      message: nested.message || 'This pair of invoices cannot be merged.',
    };
  }

  if (typeof nested === 'string' && nested.length > 0) {
    return { message: nested };
  }

  if (typeof error?.message === 'string' && error.message !== '[object Object]' && error.message.length > 0) {
    return { message: error.message };
  }

  return { message: 'Failed to merge invoices.' };
}

/**
 * Recomputes a resulting total from the union of both invoices' line items
 * (amount + tax_amount per line), rather than trusting either invoice's
 * cached `total_amount` field. Mirrors R2's own recompute-from-lines
 * approach (services/invoice_merge_execution.py) instead of assuming the two
 * pre-merge cached totals are trustworthy -- see R2 output.md's
 * "AC3 — recompute from lines" section for why that assumption is the wrong
 * one to make.
 */
export function computeMergeResultingTotal(
  items: Array<{ amount: number; tax_amount: number }>
): number {
  return items.reduce((sum, item) => sum + (item.amount ?? 0) + (item.tax_amount ?? 0), 0);
}

/**
 * True when the server answered a legacy merge call with its review-required refusal
 * (the reviewed-merge gate is on and an old client sent a bare merge). InvoiceListPage answers it by opening the
 * review for that very pair (R4 AC7); nothing was merged.
 */
export function isMergeReviewRequired(error: any): boolean {
  return extractMergeRefusal(error).code === 'merge_review_required';
}
